import { useEffect, useState } from "react";
import { api, apiErr } from "../lib/api";
import { fmtAppt } from "../lib/datetime";
import { toast } from "sonner";
import { CalendarClock, Clock, Trash2, Plus, Video, Languages, ArrowLeftRight, Copy, Loader2 } from "lucide-react";

const inp = "w-full rounded-md border border-line px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-clinical/30";

const TR_LANGS = [
  { value: "shona", label: "Shona" },
  { value: "ndebele", label: "Ndebele" },
  { value: "english", label: "English" },
];

export function TranslateTool() {
  const [text, setText] = useState("");
  const [source, setSource] = useState("");
  const [target, setTarget] = useState("english");
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!text.trim()) return toast.error("Enter some text to translate.");
    setBusy(true); setOut("");
    try {
      const { data } = await api.post("/ai/translate", { text: text.trim(), target, source: source || undefined });
      setOut(data.translation);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  const copy = () => { navigator.clipboard?.writeText(out); toast.success("Copied"); };
  return (
    <div data-testid="translate-tool">
      <h2 className="font-heading font-bold text-xl text-ink mb-1 flex items-center gap-2"><ArrowLeftRight className="w-5 h-5 text-clinical" /> Translate</h2>
      <p className="text-muted2 text-sm mb-4">One-tap translation between Shona, Ndebele and English to help with messages and consultation notes.</p>
      <div className="bg-white border border-line rounded-md p-4 grid gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-xs font-bold uppercase tracking-wider text-muted2">From
            <select value={source} onChange={(e) => setSource(e.target.value)} data-testid="translate-source" className={`${inp} mt-1`}>
              <option value="">Auto-detect</option>
              {TR_LANGS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
            </select>
          </label>
          <label className="text-xs font-bold uppercase tracking-wider text-muted2">To
            <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="translate-target" className={`${inp} mt-1`}>
              {TR_LANGS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
            </select>
          </label>
        </div>
        <textarea value={text} onChange={(e) => setText(e.target.value)} data-testid="translate-input" rows={4} placeholder="Type or paste text to translate…" className={inp} />
        <button onClick={run} disabled={busy} data-testid="translate-btn" className="inline-flex items-center justify-center gap-1.5 bg-clinical text-white rounded-md px-4 py-2 text-sm font-semibold disabled:opacity-50">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Translating…</> : <><ArrowLeftRight className="w-4 h-4" /> Translate</>}
        </button>
        {out && (
          <div className="border border-line rounded-md p-3 bg-sand/40 relative" data-testid="translate-output">
            <button onClick={copy} data-testid="translate-copy" title="Copy" className="absolute top-2 right-2 text-muted2 hover:text-clinical"><Copy className="w-4 h-4" /></button>
            <p className="text-sm text-ink whitespace-pre-wrap pr-6">{out}</p>
          </div>
        )}
      </div>
    </div>
  );
}


export function InterpreterAssignments({ onJoin }) {
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    try { const { data } = await api.get("/me/interpreter-appointments"); setAppts(data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const upcoming = appts.filter((a) => a.status === "scheduled");
  const past = appts.filter((a) => a.status !== "scheduled");
  return (
    <div data-testid="interpreter-assignments">
      <h2 className="font-heading font-bold text-xl text-ink mb-1 flex items-center gap-2"><CalendarClock className="w-5 h-5 text-clinical" /> My interpreting assignments</h2>
      <p className="text-muted2 text-sm mb-4">Consultations you have been assigned to interpret. Join when the consultation is ready.</p>
      <div className="grid gap-3">
        {upcoming.map((a) => (
          <div key={a.id} data-testid="interpreter-appt-row" className="bg-white border border-line rounded-md p-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <span className="font-heading font-bold text-ink">{a.patient_name}</span>
              <p className="text-sm text-muted2 mt-1 inline-flex items-center gap-2">
                <Video className="w-4 h-4" /> {a.specialist_name} · {a.mode} · {fmtAppt(a)}
              </p>
              {a.language && <span data-testid="interpreter-appt-language" className="inline-flex items-center gap-1 mt-1.5 text-xs font-semibold bg-clinical/10 text-clinical rounded-full px-2 py-0.5"><Languages className="w-3 h-3" /> {a.language}</span>}
            </div>
            {a.mode !== "walk_in" && (
              <button onClick={() => onJoin(a)} data-testid="interpreter-join-btn" className="inline-flex items-center gap-1.5 bg-clinical text-white text-sm font-semibold rounded-md px-4 py-2"><Video className="w-4 h-4" /> Join consultation</button>
            )}
          </div>
        ))}
        {!loading && upcoming.length === 0 && <p className="text-muted2" data-testid="interpreter-no-assignments">No upcoming assignments.</p>}
      </div>
      {past.length > 0 && (
        <div className="mt-6">
          <h3 className="text-sm font-bold text-muted2 uppercase tracking-wider mb-2">Past</h3>
          <div className="grid gap-2">
            {past.map((a) => (
              <div key={a.id} className="bg-white/60 border border-line rounded-md px-4 py-2.5 text-sm text-muted2 flex justify-between">
                <span>{a.patient_name} · {a.specialist_name}</span><span>{fmtAppt(a)} · {a.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function InterpreterAvailability() {
  const [slots, setSlots] = useState([]);
  const [form, setForm] = useState({ date: "", start_time: "", end_time: "" });
  const [saving, setSaving] = useState(false);
  const load = async () => { try { const { data } = await api.get("/me/interpreter-availability"); setSlots(data); } catch (e) { console.error(e); } };
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    if (!form.date || !form.start_time || !form.end_time) return toast.error("Fill in the date and times.");
    setSaving(true);
    try { await api.post("/me/interpreter-availability", form); setForm({ date: "", start_time: "", end_time: "" }); load(); toast.success("Availability added"); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };
  const del = async (id) => { try { await api.delete(`/me/interpreter-availability/${id}`); load(); } catch (e) { toast.error(apiErr(e)); } };
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div data-testid="interpreter-availability">
      <h2 className="font-heading font-bold text-xl text-ink mb-1 flex items-center gap-2"><Clock className="w-5 h-5 text-clinical" /> My availability</h2>
      <p className="text-muted2 text-sm mb-4">Optional — let schedulers know when you are free. You can still be booked even without setting availability.</p>
      <form onSubmit={add} className="bg-white border border-line rounded-md p-4 grid gap-3 sm:grid-cols-4 items-end mb-5" data-testid="interpreter-availability-form">
        <label className="block sm:col-span-2"><span className="text-xs uppercase tracking-wider font-bold text-muted2">Date</span>
          <input type="date" min={today} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={`${inp} mt-1`} data-testid="avail-date" /></label>
        <label className="block"><span className="text-xs uppercase tracking-wider font-bold text-muted2">From</span>
          <input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className={`${inp} mt-1`} data-testid="avail-start" /></label>
        <label className="block"><span className="text-xs uppercase tracking-wider font-bold text-muted2">To</span>
          <input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className={`${inp} mt-1`} data-testid="avail-end" /></label>
        <button type="submit" disabled={saving} data-testid="avail-add-btn" className="sm:col-span-4 inline-flex items-center justify-center gap-1.5 bg-clinical text-white rounded-md px-4 py-2 text-sm font-semibold disabled:opacity-50"><Plus className="w-4 h-4" /> Add slot</button>
      </form>
      <div className="grid gap-2">
        {slots.map((s) => (
          <div key={s.id} data-testid="avail-slot" className="flex items-center justify-between bg-white border border-line rounded-md px-4 py-2.5 text-sm">
            <span className="font-semibold text-ink">{s.date} · {s.start_time}–{s.end_time}</span>
            <button onClick={() => del(s.id)} data-testid="avail-delete" className="text-critical hover:bg-critical/10 rounded-full p-1"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {slots.length === 0 && <p className="text-muted2 text-sm">No availability slots added.</p>}
      </div>
    </div>
  );
}
